import { h } from "../core/dom.js";

// Lightweight inline-SVG charts. No dependencies.

export function lineChart(points, { height = 140, format, goal, levels, markers, xScale = "index", xFormat } = {}) {
  // points: [{ x: Date|number|string label, y: number }]
  // levels: [{ y, label, color }] — labelled horizontal reference lines (e.g. strength-band
  // thresholds); included in the y-range so every passed line is visible.
  // markers: [{ x, color }] — thin vertical rules at x positions (e.g. the moment each set was
  // completed, along a heart-rate curve). Time scale only: an index axis has no shared
  // coordinate to place them on. Drawn under the line so they never obscure the data, and they
  // do NOT widen the x-range — a marker outside it is dropped rather than stretching the plot.
  if (!points.length) return h("div", { class: "dim center" }, "No data yet");
  const W = 320, H = height;
  const useTime = xScale === "time";
  const pad = useTime ? { l: 40, r: 8, t: 12, b: 26 } : { l: 8, r: 8, t: 12, b: 18 };
  const plotted = useTime
    ? points
        .map((p) => ({ ...p, _x: timeValue(p.x) }))
        .filter((p) => Number.isFinite(p._x))
        .sort((a, b) => a._x - b._x)
    : points;
  if (!plotted.length) return h("div", { class: "dim center" }, "No data yet");

  const hasGoal = goal != null && isFinite(goal);
  const lvls = (levels || []).filter((l) => l && isFinite(l.y));
  const ys = plotted.map((p) => p.y);
  if (hasGoal) ys.push(goal); // keep the goal line within the visible range
  for (const l of lvls) ys.push(l.y);
  const min = Math.min(...ys), max = Math.max(...ys);
  const range = max - min || 1;
  const innerW = W - pad.l - pad.r, innerH = H - pad.t - pad.b;
  const minX = useTime ? plotted[0]._x : 0;
  const maxX = useTime ? plotted[plotted.length - 1]._x : plotted.length - 1;
  const xRange = maxX - minX;
  const xFor = (p, i) => pad.l + (
    useTime
      ? (xRange ? ((p._x - minX) / xRange) * innerW : innerW / 2)
      : (plotted.length === 1 ? innerW / 2 : (i / (plotted.length - 1)) * innerW)
  );
  const yFor = (v) => pad.t + innerH - ((v - min) / range) * innerH;

  const line = plotted.map((p, i) => `${i === 0 ? "M" : "L"}${xFor(p, i).toFixed(1)},${yFor(p.y).toFixed(1)}`).join(" ");
  const area = `${line} L${xFor(plotted[plotted.length - 1], plotted.length - 1).toFixed(1)},${pad.t + innerH} L${xFor(plotted[0], 0).toFixed(1)},${pad.t + innerH} Z`;
  const gy = hasGoal ? yFor(goal) : 0;
  const timeTicks = useTime ? axisTicks(minX, maxX) : [];
  const tickLabel = xFormat || shortDate;
  const xAt = (v) => pad.l + (xRange ? ((v - minX) / xRange) * innerW : innerW / 2);
  const marks = useTime
    ? (markers || [])
        .map((m) => ({ ...m, _x: timeValue(m.x) }))
        .filter((m) => Number.isFinite(m._x) && m._x >= minX && m._x <= maxX)
    : [];

  return h(
    "svg",
    { class: "chart", viewBox: `0 0 ${W} ${H}`, preserveAspectRatio: "none" },
    h("path", { class: "c-area", d: area }),
    ...marks.map((m) => h("line", {
      class: "c-mark", x1: xAt(m._x).toFixed(1), y1: pad.t, x2: xAt(m._x).toFixed(1), y2: pad.t + innerH,
      ...(m.color ? { stroke: m.color } : {}),
    })),
    ...timeTicks.map((tick, i) => {
      const tx = pad.l + (xRange ? ((tick - minX) / xRange) * innerW : innerW / 2);
      const anchor = i === 0 ? "start" : i === timeTicks.length - 1 ? "end" : "middle";
      return h("g", null,
        h("line", { class: "c-time-grid", x1: tx.toFixed(1), y1: pad.t, x2: tx.toFixed(1), y2: pad.t + innerH }),
        h("text", { class: "c-label", x: tx.toFixed(1), y: H - 5, "text-anchor": anchor }, tickLabel(tick))
      );
    }),
    useTime ? h("line", { class: "c-axis", x1: pad.l, y1: pad.t + innerH, x2: W - pad.r, y2: pad.t + innerH }) : null,
    ...lvls.map((l) => {
      const ly = yFor(l.y);
      return h("g", null,
        h("line", { class: "c-level", x1: pad.l, y1: ly.toFixed(1), x2: W - pad.r, y2: ly.toFixed(1), stroke: l.color || "var(--text-3)" }),
        l.label ? h("text", { class: "c-level-label", x: W - pad.r, y: (ly - 3).toFixed(1), "text-anchor": "end", fill: l.color || "var(--text-3)" }, l.label) : null
      );
    }),
    h("path", { class: "c-line", d: line }),
    hasGoal ? h("line", { class: "c-goal", x1: pad.l, y1: gy.toFixed(1), x2: W - pad.r, y2: gy.toFixed(1) }) : null,
    hasGoal ? h("text", { class: "c-goal-label", x: W - pad.r, y: (gy - 3).toFixed(1), "text-anchor": "end" }, `🎯 ${format ? format(goal) : Math.round(goal)}`) : null,
    ...plotted.map((p, i) => h("circle", { class: "c-dot", cx: xFor(p, i), cy: yFor(p.y), r: plotted.length > 20 ? 0 : 3 })),
    h("text", {
      class: "c-label",
      x: useTime ? pad.l - 5 : pad.l,
      y: useTime ? pad.t + 3 : 10,
      "text-anchor": useTime ? "end" : "start",
    }, format ? format(max) : String(Math.round(max))),
    h("text", {
      class: "c-label",
      x: useTime ? pad.l - 5 : pad.l,
      y: useTime ? pad.t + innerH : H - 4,
      "text-anchor": useTime ? "end" : "start",
    }, format ? format(min) : String(Math.round(min)))
  );
}

function timeValue(value) {
  if (value instanceof Date) return value.getTime();
  if (typeof value === "number") return value;
  if (typeof value !== "string") return NaN;
  // SQLite timestamps are UTC but omit the ISO `T` and zone.
  const normalized = value.includes("T") || /Z$|[+-]\d\d:\d\d$/.test(value)
    ? value
    : `${value.replace(" ", "T")}Z`;
  return new Date(normalized).getTime();
}

export function axisTicks(min, max) {
  if (!Number.isFinite(min) || !Number.isFinite(max)) return [];
  if (min === max) return [min];
  return [min, min + (max - min) / 2, max];
}

function shortDate(value) {
  return new Date(value).toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

export function barChart(points, { height = 140, format, xFormat } = {}) {
  const W = 320, H = height, pad = { l: 8, r: 8, t: 12, b: 18 };
  if (!points.length) return h("div", { class: "dim center" }, "No data yet");
  const max = Math.max(...points.map((p) => p.y)) || 1;
  const innerW = W - pad.l - pad.r, innerH = H - pad.t - pad.b;
  const bw = innerW / points.length;
  return h(
    "svg",
    { class: "chart", viewBox: `0 0 ${W} ${H}` },
    ...points.map((p, i) => {
      const barH = (p.y / max) * innerH;
      return h("rect", {
        class: "c-bar",
        x: pad.l + i * bw + bw * 0.18,
        y: pad.t + innerH - barH,
        width: bw * 0.64,
        height: Math.max(1, barH),
        rx: 3,
      });
    }),
    format ? h("text", { class: "c-label", x: pad.l, y: 10 }, format(max)) : null,
    xFormat ? h("text", { class: "c-label", x: pad.l, y: H - 4 }, xFormat(points[0].x)) : null,
    xFormat && points.length > 1
      ? h("text", { class: "c-label", x: W - pad.r, y: H - 4, "text-anchor": "end" }, xFormat(points[points.length - 1].x))
      : null
  );
}
